import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Mail, 
  MailOpen, 
  Trash2, 
  Phone, 
  User, 
  Calendar,
  MessageSquare,
  RefreshCw,
  Search,
  CheckCircle,
  X,
  LogOut
} from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminDashboard({ token, onLogout }) {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const fetchContacts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/contacts`, authHeader);
      setContacts(response.data.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)));
    } catch (error) {
      if (error.response?.status === 401) {
        onLogout();
        return;
      }
      console.error('Error fetching contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const markAsRead = async (contactId) => {
    try {
      await axios.patch(`${API_URL}/api/contacts/${contactId}/read`, {}, authHeader);
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, read: true } : c
      ));
      if (selectedContact?.id === contactId) {
        setSelectedContact({ ...selectedContact, read: true });
      }
    } catch (error) {
      console.error('Error marking as read:', error);
    }
  };

  const deleteContact = async (contactId) => {
    try {
      await axios.delete(`${API_URL}/api/contacts/${contactId}`, authHeader);
      setContacts(contacts.filter(c => c.id !== contactId));
      if (selectedContact?.id === contactId) {
        setSelectedContact(null);
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('fr-FR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  };

  const filteredContacts = contacts.filter(contact => {
    const matchesSearch = 
      contact.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contact.subject.toLowerCase().includes(searchTerm.toLowerCase());
    
    if (filter === 'unread') return matchesSearch && !contact.read;
    if (filter === 'read') return matchesSearch && contact.read;
    return matchesSearch;
  });

  const unreadCount = contacts.filter(c => !c.read).length;

  return (
    <div className="min-h-screen bg-[#162033] text-white">
      {/* Header */}
      <div className="bg-surface/50 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link 
                to="/" 
                className="flex items-center gap-2 text-sm text-slate-400 hover:text-cyan-400 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                Retour au site
              </Link>
              <div className="h-6 w-px bg-white/10" />
              <h1 className="font-unbounded font-bold text-xl">
                Administration
              </h1>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={fetchContacts}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/[0.04] border border-white/[0.08] hover:border-cyan-500/40 transition-colors text-sm"
              >
                <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
              <button
                onClick={onLogout}
                className="flex items-center gap-2 px-4 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 hover:bg-red-500/20 transition-colors text-sm"
              >
                <LogOut className="w-4 h-4" />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-cyan-500/10 flex items-center justify-center">
                <MessageSquare className="w-5 h-5 text-cyan-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contacts.length}</p>
                <p className="text-xs text-slate-500">Total messages</p>
              </div>
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <Mail className="w-5 h-5 text-emerald-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{unreadCount}</p>
                <p className="text-xs text-slate-500">Non lus</p>
              </div>
            </div>
          </div>
          <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
                <CheckCircle className="w-5 h-5 text-violet-400" />
              </div>
              <div>
                <p className="text-2xl font-bold">{contacts.length - unreadCount}</p>
                <p className="text-xs text-slate-500">Traités</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-col md:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
            <input
              type="text"
              placeholder="Rechercher par nom, email ou sujet..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 rounded-lg bg-white/[0.02] border border-white/[0.08] text-white placeholder-slate-500 focus:border-cyan-500/40 focus:outline-none transition-colors text-sm"
            />
          </div>
          <div className="flex gap-2">
            {['all', 'unread', 'read'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                  filter === f 
                    ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30' 
                    : 'bg-white/[0.02] border border-white/[0.08] text-slate-400 hover:text-white'
                }`}
              >
                {f === 'all' ? 'Tous' : f === 'unread' ? 'Non lus' : 'Lus'}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          {/* Contact List */}
          <div className="lg:col-span-2 bg-white/[0.02] border border-white/[0.06] rounded-2xl overflow-hidden">
            <div className="max-h-[600px] overflow-y-auto">
              {loading ? (
                <div className="p-8 text-center">
                  <RefreshCw className="w-8 h-8 text-slate-500 animate-spin mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Chargement...</p>
                </div>
              ) : filteredContacts.length === 0 ? (
                <div className="p-8 text-center">
                  <Mail className="w-8 h-8 text-slate-600 mx-auto mb-2" />
                  <p className="text-sm text-slate-500">Aucun message trouvé</p>
                </div>
              ) : (
                filteredContacts.map((contact) => (
                  <motion.div
                    key={contact.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => {
                      setSelectedContact(contact);
                      if (!contact.read) markAsRead(contact.id);
                    }}
                    className={`p-4 border-b border-white/5 cursor-pointer transition-colors ${
                      selectedContact?.id === contact.id 
                        ? 'bg-cyan-500/10' 
                        : 'hover:bg-white/[0.02]'
                    } ${!contact.read ? 'bg-white/[0.02]' : ''}`}
                  >
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${
                        contact.read ? 'bg-slate-600' : 'bg-cyan-400'
                      }`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 mb-1">
                          <p className={`font-medium text-sm truncate ${
                            contact.read ? 'text-slate-400' : 'text-white'
                          }`}>
                            {contact.name}
                          </p>
                          <span className="text-[10px] text-slate-500 flex-shrink-0">
                            {formatDate(contact.created_at).split(',')[0]}
                          </span>
                        </div>
                        <p className={`text-xs truncate mb-1 ${
                          contact.read ? 'text-slate-500' : 'text-slate-300'
                        }`}>
                          {contact.subject}
                        </p>
                        <p className="text-xs text-slate-600 truncate">
                          {contact.message}
                        </p>
                      </div>
                    </div>
                  </motion.div>
                ))
              )}
            </div>
          </div>

          {/* Contact Detail */}
          <div className="lg:col-span-3">
            <AnimatePresence mode="wait">
              {selectedContact ? (
                <motion.div
                  key={selectedContact.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-6"
                >
                  {/* Header */}
                  <div className="flex items-start justify-between mb-6">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <h2 className="font-unbounded font-semibold text-lg">
                          {selectedContact.name}
                        </h2>
                        {selectedContact.read ? (
                          <span className="px-2 py-0.5 rounded-full bg-slate-500/20 text-slate-400 text-[10px]">
                            Lu
                          </span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full bg-cyan-500/20 text-cyan-400 text-[10px]">
                            Nouveau
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-slate-400">{selectedContact.subject}</p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => deleteContact(selectedContact.id)}
                        className="p-2 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-colors"
                        title="Supprimer"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setSelectedContact(null)}
                        className="p-2 rounded-lg bg-white/[0.04] text-slate-400 hover:text-white transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Contact Info */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 p-4 bg-white/[0.02] rounded-xl">
                    <div className="flex items-center gap-3">
                      <Mail className="w-4 h-4 text-cyan-400" />
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase">Email</p>
                        <a 
                          href={`mailto:${selectedContact.email}`}
                          className="text-sm text-cyan-400 hover:underline"
                        >
                          {selectedContact.email}
                        </a>
                      </div>
                    </div>
                    {selectedContact.phone && (
                      <div className="flex items-center gap-3">
                        <Phone className="w-4 h-4 text-emerald-400" />
                        <div>
                          <p className="text-[10px] text-slate-500 uppercase">Téléphone</p>
                          <a 
                            href={`tel:${selectedContact.phone}`}
                            className="text-sm text-emerald-400 hover:underline"
                          >
                            {selectedContact.phone}
                          </a>
                        </div>
                      </div>
                    )}
                    <div className="flex items-center gap-3">
                      <Calendar className="w-4 h-4 text-violet-400" />
                      <div>
                        <p className="text-[10px] text-slate-500 uppercase">Reçu le</p>
                        <p className="text-sm text-slate-300">
                          {formatDate(selectedContact.created_at)}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Message */}
                  <div>
                    <p className="text-[10px] text-slate-500 uppercase mb-2">Message</p>
                    <div className="p-4 bg-white/[0.02] rounded-xl">
                      <p className="text-sm text-slate-300 leading-relaxed whitespace-pre-wrap">
                        {selectedContact.message}
                      </p>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="mt-6 flex gap-3">
                    <a
                      href={`mailto:${selectedContact.email}?subject=Re: ${selectedContact.subject}`}
                      className="flex-1 flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 text-void font-semibold text-sm hover:shadow-lg hover:shadow-cyan-500/20 transition-all"
                    >
                      <Mail className="w-4 h-4" />
                      Répondre par email
                    </a>
                    {selectedContact.phone && (
                      <a
                        href={`tel:${selectedContact.phone}`}
                        className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-white/[0.04] border border-white/[0.08] text-white font-medium text-sm hover:border-emerald-500/40 transition-all"
                      >
                        <Phone className="w-4 h-4" />
                        Appeler
                      </a>
                    )}
                  </div>
                </motion.div>
              ) : (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="bg-white/[0.02] border border-white/[0.06] rounded-2xl p-12 text-center"
                >
                  <MessageSquare className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                  <p className="text-slate-500">Sélectionnez un message pour voir les détails</p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
}
